import { InviteStatus, getInviteStatus, getInviteStatusTooltip } from "@utils/providerInviteStatus";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Badge, Button, Column, Paragraph, Small, Table, Tooltip } from "@fronterahealth/frontera-ui-components";

import { useLearnerByIdQuery, useUpdateLearnerProvidersMutation } from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { AddLearnerProviders } from "@pages/ClientDetailsSubPages/ClientProviders/AddLearnerProviders";

interface ClientProvidersProps {}

export interface AssignableLearnerProvidersType {
  id: string;
  name: string;
  email: string;
  roles: string;
  intakeInvite?: string | null;
  invitationUrl?: string | null;
  auth0Id?: string | null;
}

export const ClientProviders: React.FC<ClientProvidersProps> = ({}) => {
  const { clientId } = useParams();
  const [isNewProviderDialogueOpen, setIsNewProviderDialogueOpen] = useState<boolean>(false);
  const [learnerProvidersList, setLearnerProvidersList] = useState<AssignableLearnerProvidersType[]>([]);
  const [allProviderIds, setAllProviderIds] = useState<string[]>([]);

  const learnerDetailsQuery = useLearnerByIdQuery(
    { learnerId: clientId ? clientId : "" },
    {
      queryKey: ["learner-by-id", clientId],
      enabled: !!clientId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );
  const { data, refetch, isFetching } = learnerDetailsQuery;

  const updateLearnerProviderMutation = useUpdateLearnerProvidersMutation({
    onSuccess: (data) => {
      if (data.updateLearnerProviders?.status) {
        notifySuccess(data?.updateLearnerProviders?.message || "");
        refetch();
      } else {
        notifyError("Failed to remove provider!");
      }
    },
  });

  const { mutate: updateLearnerProvider } = updateLearnerProviderMutation;

  const columns: Column<AssignableLearnerProvidersType>[] = [
    {
      title: "Provider Name",
      key: "provider_name",
      dataIndex: "name",
    },
    {
      title: "Email",
      key: "email",
      dataIndex: "email",
    },
    {
      title: "Type",
      dataIndex: "roles",
      key: "roles",
    },
    {
      title: "Invite Status",
      dataIndex: "intakeInvite",
      key: "intakeInvite",
      render: (_, record) => {
        const status = getInviteStatus(record.intakeInvite || "");
        return (
          <Tooltip
            showInfoIcon={false}
            disabled={status !== InviteStatus.NotInvited && status !== InviteStatus.Pending}
            text={getInviteStatusTooltip(status)}
          >
            <Badge text={getInviteStatus(record.intakeInvite || "")} appearance="neutral" />
          </Tooltip>
        );
      },
    },
  ];

  useEffect(() => {
    if (data && data.learnerById) {
      // Store ALL provider IDs (including archived) for mutations to avoid silently unmapping archived providers
      setAllProviderIds(
        data.learnerById.learnerproviderSet.edges.map((edge) => edge?.node?.provider?.id || "").filter(Boolean),
      );
      setLearnerProvidersList(
        data.learnerById.learnerproviderSet.edges
          .filter((edge) => edge?.node?.provider?.archived !== true)
          .map((edge) => {
            const provider = edge?.node?.provider;
            const providerMetadata = provider?.providermetadata;
            const roles = (() => {
              const rolesStringArray = provider?.userRoles;

              if (!Array.isArray(rolesStringArray)) return ""; // Ensure it's an array

              try {
                const roleNames = rolesStringArray
                  .map((role) => JSON.parse(role)?.name)
                  .filter((name) => name === "bcba" || name === "rbt")
                  .map((name) => name.toUpperCase()); // Remove any null/undefined values

                return roleNames.join(", "); // Join role names into a single string
              } catch {
                return ""; // Return empty string on error
              }
            })();
            const inviteStatus = providerMetadata?.invitationStatus;

            return {
              id: provider?.id || "",
              name: `${providerMetadata?.firstName} ${providerMetadata?.lastName}`,
              roles: roles,
              email: providerMetadata?.email || "",
              intakeInvite: inviteStatus || null,
              invitationUrl: providerMetadata?.invitationUrl || null,
              auth0Id: provider?.auth0User?.auth0id,
            };
          }),
      );
    }
  }, [data]);

  return (
    <div className="flex w-full overflow-x-hidden flex-col items-start gap-4 py-4">
      <div className="flex w-full justify-end">
        <Button
          text="Add Provider"
          variant="primary"
          buttonStyle="border"
          size="md"
          onClick={() => {
            setIsNewProviderDialogueOpen(true);
          }}
          disabled={isFetching}
        />
      </div>
      <div className="flow-root bg-white w-full">
        <Table
          data={learnerProvidersList}
          columns={columns}
          loading={isFetching}
          actions={[
            {
              label: "View provider profile",
              callback: (provider) => {
                window.open(`/members/provider/${provider.id}/details`, "_blank");
              },
            },
            {
              label: "Unassign Provider",
              callback: (provider) => {
                updateLearnerProvider({
                  learnerId: clientId || "",
                  providerIds: allProviderIds.filter((id) => id !== provider.id),
                });
              },
            },
          ]}
          emptyMessage={
            <div className="flex flex-col items-center">
              <Paragraph displayType="loud">No Providers Added/Mapped Yet</Paragraph>
              <Small>There are no providers mapped to this client yet.</Small>
            </div>
          }
          showPagination
          showSearch
        />
      </div>
      <AddLearnerProviders
        isDialogOpen={isNewProviderDialogueOpen}
        setDialogOpen={setIsNewProviderDialogueOpen}
        onAddProvider={() => {
          refetch();
        }}
        selectedProviders={learnerProvidersList}
        clientId={clientId}
        archivedProviderIds={allProviderIds.filter((id) => !learnerProvidersList.some((p) => p.id === id))}
      />
    </div>
  );
};
